/* import __COLOCATED_TEMPLATE__ from './specifications.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { userEntryToNumber } from '@ebiz/pretty-num';
import FilterConstants from '../../filter-constants';
import { formatFieldValue } from '../../utils/format';
import ErrorTracker from '../../utils/error-tracker';
import type SpecificationsInput from '../../models/inputs/specification';
import type SpecificationsAdvisor from '../../models/advisors/specification';
import { getResponseType, getResponseValue } from '../../models/gestures/filter-response-gesture';
import { changeGainUnits } from '../../models/gestures/gain-unit-gesture';
import type IntlService from 'ember-intl/services/intl';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import { tracked as makeTracked } from 'tracked-built-ins';
import { FilterType, filters } from '../../models/filter/filter';

const gainUnitValues = [
  { text: 'settings.specifications.gain-units.db', value: FilterConstants.gainUnits.db },
  { text: 'settings.specifications.gain-units.vv', value: FilterConstants.gainUnits.vv },
];

interface SpecificationsSettingsArgs {
  model?: SpecificationsInput;
  advisor?: SpecificationsAdvisor;
  updateModel?: (inputType: string, modeldata: SpecificationsInput) => void;
  updateErrors?: (inputType: string, errors: Array<ValidationError>) => void;
  takeStep?: (requestedAction: string) => void;
  showHelp?: (title: string, message: string, msgparams?: Record<string, unknown>) => void;
  tool?: string;
}

///////////////////////////////////////////////////////////////////////////////////////
// settings-specifications - The panel that lets the user set filter specifications  //
///////////////////////////////////////////////////////////////////////////////////////

export default class SpecificationsSettings extends Component<SpecificationsSettingsArgs> {
  @service declare intl: IntlService;
  @service('aditools-site') declare siteconfig: SiteService;

  @tracked workingModel: SpecificationsInput; // a model holding the values of all the fields that can be parsed
  @tracked errorTracker: ErrorTracker;

  fieldNames: { [key: string]: string }; // Field Names for Input Textboxes. Taken from Filter Constants
  fieldTypes: { [key: string]: string }; // Field Types for Parsing and Formatting. Taken from Filter Constants
  fieldFormat: string; // Field Format for The Input Boxes in the Component

  @tracked stagesDisplayText: string; // Display of working number of stages
  @tracked filterResponseDisplayText: string | undefined; // Display of working filter response
  @tracked filterResponseHoverText: string; // Hover text for filter response slider

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  constructor(owner: unknown, args: SpecificationsSettingsArgs) {
    super(owner, args);
    assert('`Specifications Input Model` is required', !isNone(this.args.model));
    assert('`Specifications Advisor` is required', !isNone(this.args.advisor));
    this.workingModel = makeTracked(this.args.advisor.clone(this.args.model) as SpecificationsInput);
    this.filterResponseHoverText = '';
    this.filterResponseDisplayText = this.getFilterResponseDisplayText();
    this.stagesDisplayText = this.getStagesDisplayText();
    this.fieldNames = FilterConstants.fields.inputs.specifications;
    this.fieldTypes = this.initFieldTypes();
    this.fieldFormat = '3s';
    this.errorTracker = new ErrorTracker(FilterConstants.tabs.specifications, this.args.advisor);
    next(this, () => {
      const errors = this.errorTracker.validate(this.workingModel);
      if (errors.length > 0 && this.args.updateErrors) {
        this.args.updateErrors('specifications', errors);
      }
    });
  }

  initFieldTypes(): { [key: string]: string } {
    return {
      gain: FilterConstants.fieldTypes.NumericDecimal,
      passbandAttenuation: FilterConstants.fieldTypes.NumericDecimal,
      passbandFrequency: FilterConstants.fieldTypes.Engineering,
      stopbandAttenuation: FilterConstants.fieldTypes.NumericDecimal,
      stopbandFrequency: FilterConstants.fieldTypes.Engineering,
      centerFrequency: FilterConstants.fieldTypes.Engineering,
      filterResponseValue: FilterConstants.fieldTypes.NumericDecimal,
    };
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Conditions (Returns true or false)                                         //
  ////////////////////////////////////////////////////////////////////////////////

  get isGainValid(): boolean {
    return !this.errorTracker.isInErrorList('gain');
  }
  get isPassbandAttenuationValid(): boolean {
    return !this.errorTracker.isInErrorList('passbandAttenuation');
  }
  get isPassbandFrequencyValid(): boolean {
    return !this.errorTracker.isInErrorList('passbandFrequency');
  }
  get isStopbandAttenuationValid(): boolean {
    return !this.errorTracker.isInErrorList('stopbandAttenuation');
  }
  get isStopbandFrequencyValid(): boolean {
    return !this.errorTracker.isInErrorList('stopbandFrequency');
  }
  get isCenterFrequencyValid(): boolean {
    return !this.errorTracker.isInErrorList('centerFrequency');
  }
  get isFilterResponseValid(): boolean {
    return !this.errorTracker.isInErrorList('filterResponse');
  }
  get hasCenterFrequency(): boolean {
    return (
      typeof (this.args.model as SpecificationsInput).centerFrequency !== 'undefined' &&
      (this.args.model as SpecificationsInput).centerFrequency !== null &&
      !isNaN((this.args.model as SpecificationsInput).centerFrequency)
    );
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Properties                                                                 //
  ////////////////////////////////////////////////////////////////////////////////

  get unitsSelection(): { text: string; value: string } | undefined {
    const units = this.workingModel.gainUnit;
    const selection = this.gainUnitValues.find((item) => {
      return item.value === units;
    });
    return selection;
  }
  get filterResponseDisplayValue(): string {
    const value = this.workingModel.filterResponseValue;
    return value ? formatFieldValue(value, FilterConstants.fieldTypes.NumericDecimal, '2dz') : '';
  }

  get gainUnitValues(): Array<{ text: string; value: string }> {
    return gainUnitValues;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Utility Functions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  // Function that does the following:
  // - Calls Error Tracker to Validate the inputs in the Component
  // - Updates the Error Section if there are errors found
  // - Updates the Model if there are no errors found.
  validateAndUpdateModel(): void {
    // Validate the Inputs
    const errors = this.errorTracker.validate(this.workingModel);

    // Update the error list when there are errors, if there are none, update the model
    if (errors.length > 0) {
      if (this.args.updateErrors) {
        this.args.updateErrors('specifications', errors);
      }
    } else {
      if (this.args.updateModel && this.args.advisor) {
        this.args.updateModel('specifications', this.args.advisor.clone(this.workingModel) as SpecificationsInput);
      }
    }

    // Refresh Response Display
    this.refreshResponse(errors.length > 0);
  }

  // Function that sets the response value and the response type from the slider to the workingModel
  updateResponseModel(): void {
    this.workingModel.filterResponseValue = getResponseValue(
      this.workingModel.filterResponseSlider,
      this.workingModel.passbandAttenuation,
    );
    this.workingModel.selectedResponseType = getResponseType(this.workingModel.filterResponseSlider);
  }

  // Function that sets the field input text to the workingModel
  updateModelInput(param: { fieldId: keyof SpecificationsInput; fieldValue: string; status: boolean }): void {
    if (param.status) {
      // Get Raw field value after input has been rounded to the correct display value (For Number Field Types Only)
      const fieldValue =
        this.fieldTypes[param.fieldId as keyof SpecificationsInput] === FilterConstants.fieldTypes.Numeric ||
        this.fieldTypes[param.fieldId as keyof SpecificationsInput] === FilterConstants.fieldTypes.NumericDecimal ||
        this.fieldTypes[param.fieldId as keyof SpecificationsInput] === FilterConstants.fieldTypes.Engineering
          ? userEntryToNumber(param.fieldValue)
          : param.fieldValue;
      this.workingModel[param.fieldId] = fieldValue;
    }
  }

  resetSliderDisplay(): void {
    this.filterResponseHoverText = '';
  }

  getFilterResponseHoverText(sliderValue: string, passbandAttenuation: string): string {
    const value = getResponseValue(sliderValue, passbandAttenuation);
    const reponseType = getResponseType(sliderValue);
    const responseValue = value ? formatFieldValue(value, FilterConstants.fieldTypes.NumericDecimal, '2dz') : '';
    if (reponseType === FilterConstants.responseType.chebyshev) {
      return `${reponseType} ${responseValue} ${this.intl.t('units.db')}`;
    } else {
      return `${reponseType} ${responseValue}`;
    }
  }

  getFilterResponseDisplayText(): string {
    const responseType = this.intl.t(
      `annotations.filterResponse.responseType.${this.workingModel.selectedResponseType}`,
    );
    let order = 0;
    try {
      const workingModel = this.workingModel;
      // const filter = new Filter(
      //   (this.args.advisor as SpecificationsAdvisor).filterType,
      //   workingModel.selectedResponseType
      // );
      order = filters[(this.args.advisor as SpecificationsAdvisor).filterType as keyof FilterType].getOrder({
        filterResponseType: workingModel.selectedResponseType,
        passbandFrequency: workingModel.passbandFrequency,
        passbandAttenuation: workingModel.passbandAttenuation,
        stopbandFrequency: workingModel.stopbandFrequency,
        stopbandAttenuation: workingModel.stopbandAttenuation,
        centerFrequency: workingModel.centerFrequency,
        filterResponseValue: workingModel.filterResponseValue,
      });
    } catch (e) {
      order = 0;
    }
    let orderString = '';
    if (order <= 0 || order > FilterConstants.maxOrder) {
      return '';
    } else {
      orderString = this.intl.t(`annotations.filterResponse.orders.order${order}`, { responseType: responseType });
      if (this.workingModel.filterResponseValue) {
        let units = '';
        if (this.workingModel.selectedResponseType === FilterConstants.responseType.chebyshev) {
          units = ' ' + this.intl.t('units.db');
        }
        return `${orderString} ${this.filterResponseDisplayValue}${units}`;
      } else {
        return orderString;
      }
    }
  }

  getStagesDisplayText(): string {
    const workingModel = this.workingModel;
    let stages = 0,
      order = 0;
    try {
      // const filter = new Filter(
      //   (this.args.advisor as SpecificationsAdvisor).filterType,
      //   workingModel.selectedResponseType
      // );
      stages = filters[(this.args.advisor as SpecificationsAdvisor).filterType as keyof FilterType].getNumberOfStages({
        filterResponseType: workingModel.selectedResponseType,
        passbandFrequency: workingModel.passbandFrequency,
        passbandAttenuation: workingModel.passbandAttenuation,
        stopbandFrequency: workingModel.stopbandFrequency,
        stopbandAttenuation: workingModel.stopbandAttenuation,
        centerFrequency: workingModel.centerFrequency,
        filterResponseValue: workingModel.filterResponseValue,
      });
      order = filters[(this.args.advisor as SpecificationsAdvisor).filterType as keyof FilterType].getOrder({
        filterResponseType: workingModel.selectedResponseType,
        passbandFrequency: workingModel.passbandFrequency,
        passbandAttenuation: workingModel.passbandAttenuation,
        stopbandFrequency: workingModel.stopbandFrequency,
        stopbandAttenuation: workingModel.stopbandAttenuation,
        centerFrequency: workingModel.centerFrequency,
        filterResponseValue: workingModel.filterResponseValue,
      });
    } catch (e) {
      stages = 0;
    }
    if (stages <= 0 || order > FilterConstants.maxOrder) {
      return '';
    } else {
      return `(${this.intl.t('annotations.filterResponse.stages', { count: stages })})`;
    }
  }

  refreshResponse(hasErrors: boolean): void {
    if (!hasErrors) {
      this.filterResponseDisplayText = this.getFilterResponseDisplayText();
      this.stagesDisplayText = this.getStagesDisplayText();
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Component Actions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  @action changeGainUnits(units: { text: string; value: string }): void {
    this.workingModel.gain = changeGainUnits(this.workingModel.gain, units.value);
    this.workingModel.gainUnit = units.value;
    this.validateAndUpdateModel();
  }
  @action displayResponse(e: InputEvent): void {
    this.filterResponseHoverText = this.getFilterResponseHoverText(
      (e.target as HTMLInputElement).value,
      this.workingModel.passbandAttenuation,
    );
  }
  @action updateResponse(e: InputEvent): void {
    this.workingModel.filterResponseSlider = (e.target as HTMLInputElement).value;
    this.updateResponseModel();
    this.validateAndUpdateModel();
    this.resetSliderDisplay();
  }
  @action hideResponse(): void {
    this.resetSliderDisplay();
  }
  // Action Handler when any of the textboxes in the Specifications Settings Panel encounter a Change Event
  // Inputs: param. A structure containing the following
  //         - fieldId - the field that caused the change event to occur
  //         - fieldValue - the value corresponding to the fieldId
  //         - status - true if the parsed value of the field is valid, otherwise false
  // Description - When a field in the panel has been changed the function first validates the changed field
  //               and then calls the function to validate field values and update models when no errors are found
  @action fieldChange(param: { fieldId: keyof SpecificationsInput; fieldValue: string; status: boolean }): void {
    this.errorTracker.updateUnparseableList(param);
    this.updateResponseModel();
    this.updateModelInput(param);
    this.validateAndUpdateModel();
  }
}
